import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import i18n from "@/i18n";
import { STORAGE_KEY } from "@/i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Lingua unica condivisa: la scelta visibile/locale prevale (anche sul dispositivo);
      // il profilo viene allineato, e su un nuovo dispositivo si adotta la preferenza salvata.
      try {
        const supported = ["it", "en", "fr", "es", "de"];
        const stored = (localStorage.getItem(STORAGE_KEY) || "").slice(0, 2);
        const prof = data?.language && supported.includes(data.language) ? data.language : null;
        const apply = (lng) => { if ((i18n.language || "").slice(0, 2) !== lng) i18n.changeLanguage(lng); };
        if (stored && supported.includes(stored)) {
          localStorage.setItem(STORAGE_KEY, stored);
          apply(stored);
          if (prof !== stored) api.put("/profile", { language: stored }).catch(() => {});
        } else if (prof) {
          localStorage.setItem(STORAGE_KEY, prof);
          apply(prof);
        } else {
          const dev = (navigator.language || "it").slice(0, 2);
          const lang = supported.includes(dev) ? dev : "it";
          localStorage.setItem(STORAGE_KEY, lang);
          apply(lang);
          api.put("/profile", { language: lang }).catch(() => {});
        }
      } catch (e) { /* noop */ }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("Logout request failed (clearing session anyway):", err); }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
